import {ONE, ZERO, THRESHOLD_SETTINGS_INPUT_DEBOUNCE_MS} from "../constants";
import debounce from 'lodash.debounce';

const DRAW_MAPPED_SLIDER_MULTIPLIER = 2,
    DEFAULT_INPUT_VALUE_MULTIPLIER = 60;

const initResetToDefaultsButton = (sectionName, useDefaults, resetClickCallback) => {
  document.querySelectorAll(`#${sectionName} .js-reset-defaults`).forEach(button => {
    if (useDefaults) {
      button.classList.add('disabled');
    } else {
      button.classList.remove('disabled');
      button.addEventListener('click', () => {
        document.querySelectorAll(`#${sectionName} .sliderDisplay input`).forEach(input => {
          input.dispatchEvent(new Event('resetToDefault'));
        });

        resetClickCallback();
      });
    }
  });
}

const initSaveBind = (sectionName, sectionObjectId, collectSectionData, validationForms) => {
  const saveButton = document.querySelector(`#${sectionName} .section_edit .save_button`);
  if (saveButton) {
    const $errorMessage = $(document.querySelector(`#${sectionName} .infobox--error`));
    saveButton.addEventListener('click', () => {
      $errorMessage.hide();
      if (validationForms.some(form => !form.valid())) {
        $errorMessage.fadeIn();
        const activeTab = document.querySelector(`#${sectionName} .ui-tabs-active a`);
        if (activeTab) {
          const activeTabId = activeTab.getAttribute('href'),
              activeTabPanelError = document.querySelector(`${activeTabId} input.error`);
          if (!activeTabPanelError) {
            const allTabsA = document.querySelectorAll(`#${sectionName} .ui-tab a`);
            for (let tabIndex = 0; tabIndex < allTabsA.length; tabIndex++) {
              const tab = allTabsA[tabIndex],
                  tabPanelId = tab.getAttribute('href'),
                  tabPanelError = document.querySelector(`${tabPanelId} input.error`);
              if (tabPanelError) {
                $(document.querySelector(`#${sectionName} .ui-tabs`)).tabs('option', 'active', tabIndex);
                break;
              }
            }
          }
        }
      } else {
        nguxSettings.sectionEditSave(sectionName, sectionObjectId, collectSectionData());
      }
    });
  }
}

const initSlider = (sliderContainer, sliderId, minuteMap, minuteValMap, setVal, minuteBoundaryValue, inputValidationMessage, defaults, onSliderInputChanged) => {
  const defaultValue = defaults[sliderId],
      value = setVal || defaultValue;

  const sliderBar = sliderContainer.getElementsByClassName('sliderBar')[0],
      $sliderBar = $(sliderBar),
      input = sliderContainer.querySelector('.sliderDisplay input'),
      hiddenInput = sliderContainer.getElementsByClassName('js-threshold_minutes')[0],
      $input = $(input),
      multipliersSelect = sliderContainer.querySelector('.js-multiplier-select'),
      maxSliderValue = (minuteValMap.length * DRAW_MAPPED_SLIDER_MULTIPLIER) - ONE;

  determineMultiplierSelectValueIfRequired(multipliersSelect, value, minuteBoundaryValue);
  const inputValueMultiplierObject = {
    value: multipliersSelect ? parseInt(multipliersSelect.value) : DEFAULT_INPUT_VALUE_MULTIPLIER
  }

  const initialValueExistingInSliderObject = determineSliderValueFromInputValue(minuteMap, value);

  const updateInputs = newValue => {
    input.value = newValue / inputValueMultiplierObject.value;
    hiddenInput.value = newValue;
  }

  $sliderBar.slider({
    max: initialValueExistingInSliderObject.existingValue ? maxSliderValue : maxSliderValue - ONE,
    min: ZERO,
    step: initialValueExistingInSliderObject.existingValue ? DRAW_MAPPED_SLIDER_MULTIPLIER : 1,
    value: initialValueExistingInSliderObject.value,
    slide: (event, ui) => {
      $sliderBar.slider('option', {
        step: 2,
        max: maxSliderValue
      });
      //if step=1 and slider is between marks, ui.value will be odd when user slides between another marks,
      //cause step=2 done only after slide, so we need to ceil the result number, because it's decimal
      const mapIndex = Math.ceil(ui.value && (ui.value / DRAW_MAPPED_SLIDER_MULTIPLIER));
      determineMultiplierSelectValueIfRequired(multipliersSelect, minuteMap[mapIndex], minuteBoundaryValue);
      updateInputs(minuteMap[mapIndex]);
      $input.valid();
    },
    change: (event, ui) => {
      if (ui.value % DRAW_MAPPED_SLIDER_MULTIPLIER === 0) {
        const mapIndex = ui.value && (ui.value / DRAW_MAPPED_SLIDER_MULTIPLIER);
        updateInputs(minuteMap[mapIndex]);
      }
      input.dispatchEvent(new Event('change'));
    }
  });

  const updateSliderValue = newValue => {
    const valueExistingInSliderObject = determineSliderValueFromInputValue(minuteMap, newValue);
    if (!valueExistingInSliderObject.existingValue) {
      $sliderBar.slider('option', {
        step: 1,
        max: maxSliderValue - ONE
      });
    }
    $sliderBar.slider('value', valueExistingInSliderObject.value);
    hiddenInput.value = newValue;
  }

  const onInputChanged = () => {
    if ($input.valid()) {
      const newValueInMinutes = parseInt(input.value) * inputValueMultiplierObject.value;
      updateSliderValue(newValueInMinutes);
    }
  }

  input.addEventListener('input',debounce(() => {
    onInputChanged();
  }, THRESHOLD_SETTINGS_INPUT_DEBOUNCE_MS));

  input.addEventListener('resetToDefault', () => {
    determineMultiplierSelectValueIfRequired(multipliersSelect, defaultValue, minuteBoundaryValue);
    updateSliderValue(defaultValue);
    input.value = defaultValue / inputValueMultiplierObject.value;
    $input.valid();
  });

  input.addEventListener('change', onSliderInputChanged);

  configureInputValidation($(sliderContainer), minuteMap, minuteValMap[0], minuteValMap[minuteValMap.length - 1], inputValidationMessage, inputValueMultiplierObject);

  if (multipliersSelect) {
    multipliersSelect.addEventListener('change', () => {
      inputValueMultiplierObject.value = parseInt(multipliersSelect.value);
      onInputChanged();
    });

    multipliersSelect.addEventListener('manual-change', () => {
      inputValueMultiplierObject.value = parseInt(multipliersSelect.value);
    });
  }

  drawTicks(sliderContainer, minuteValMap, minuteMap.indexOf(defaultValue));

  updateInputs(value);
};

const determineSliderValueFromInputValue = (sliderValues, inputValue) => {
  let existingValue, value;
  // input value can't be less then first slider value and more than last slider value due to validation, so index always be not undefined
  const indexForSliderValueMoreOrEqualInputValue = sliderValues.findIndex(value => inputValue <= value);
  const sliderValue = sliderValues[indexForSliderValueMoreOrEqualInputValue];

  if (sliderValue === inputValue) {
    existingValue = true;
    value = DRAW_MAPPED_SLIDER_MULTIPLIER * indexForSliderValueMoreOrEqualInputValue;
  }
  else {
    value = DRAW_MAPPED_SLIDER_MULTIPLIER * indexForSliderValueMoreOrEqualInputValue - 1;
    existingValue = false;
  }

  return {
    existingValue,
    value
  }
}

const determineMultiplierSelectValueIfRequired = (multipliersSelect, sliderValue, boundaryValue) => {
  if (multipliersSelect) {
    const newSelectedIndex = sliderValue >= boundaryValue && (sliderValue % parseInt(multipliersSelect.options[1].value) === 0) ? 1 : 0;
    if (multipliersSelect.selectedIndex !== newSelectedIndex) {
      multipliersSelect.selectedIndex = newSelectedIndex;
      multipliersSelect.dispatchEvent(new Event('manual-change'));
    }
  }
}

const createValidationMessage = (messageToFormat, minLabel, maxLabel) => {
  return messageToFormat
      .replace(new RegExp( "{0\\}", "g" ), () => minLabel)
      .replace(new RegExp( "{1\\}", "g" ), () => maxLabel);
}

const configureInputValidation = ($form, mapValues, minValueLabel, maxValueLabel, validationMessage, inputValueMultiplierObject) => {
  const min = mapValues[0],
      max = mapValues[mapValues.length - 1],
      validationName = $form.attr('id');
  $.validator.addMethod(
      validationName,
      (value, element) => {
        const val = parseInt(element.value) * inputValueMultiplierObject.value;
        return val >= min && val <= max;
      },
      createValidationMessage(validationMessage, minValueLabel.toLowerCase(), maxValueLabel.toLowerCase()));

  $form.validate({
    ignore: [],
    errorElement: 'span',
    errorPlacement: (error, element) => {
      error.addClass('feedback16-icon error field-error js-validation-threshold');
      error.attr('data-axtooltip-class', 'error');
      error.insertAfter(element);
    },
    showErrors(errorMap, errorList) {
      this.defaultShowErrors();
      errorList.forEach(() => {
        const $validationLabel = $form.find('.js-validation-threshold');
        $validationLabel.attr('data-axtooltip', $validationLabel.text());
        $validationLabel.text('');
      });
    },
    rules: {
      threshold: {
        required: true,
        digits: true,
        [validationName]: true,
        min: false
      }
    }
  })
}

const drawTicks = (container, ticksMap, defaultValueIndex) => {
  let containerClass = 'tick-container';
  if (ticksMap.length === 5) {
    containerClass += '-5';
  } else if (ticksMap.length === 9) {
    containerClass += '-9';
  } else if (ticksMap.length === 10) {
    containerClass += '-10';
  }

  let tickContainer = container.querySelector(`.${containerClass}`);
  if (!tickContainer) {
    tickContainer = document.createElement('div');
    tickContainer.classList.add(containerClass);
    container.appendChild(tickContainer);
  }

  let ticks = '';
  for (let tickIndex = ZERO; tickIndex < ticksMap.length; tickIndex++) {
    switch (tickIndex) {
      case ZERO:
        ticks += `<div class="tick center"><span class="tick-label-absolute tick-label-absolute--left">${ticksMap[tickIndex]}</span></div>`;
        break;
      case ticksMap.length-ONE:
        ticks += `<div class="tick center"><span class="tick-label-absolute">${ticksMap[tickIndex]}</span></div>`;
        break;
      case defaultValueIndex:
        ticks += `<div class="tick default"><span class="tick-label-absolute tick-label-absolute--center">${ticksMap[tickIndex]}</span></div>`;
        break;
      default:
        ticks += `<div class="tick"></div>`;
    }
  }

  tickContainer.innerHTML = ticks;
}

export {initResetToDefaultsButton, initSaveBind, initSlider, configureInputValidation}
